/**
 * Custom hook to ensure code runs only once even in React StrictMode
 * This prevents double initialization in development
 */

import { useRef, useEffect } from 'react';

export const useStrictModeOnce = (callback: () => void | Promise<void>, deps: React.DependencyList) => {
  const hasRun = useRef(false);
  const isRunning = useRef(false);
  
  useEffect(() => {
    // Prevent multiple simultaneous executions
    if (hasRun.current || isRunning.current) {
      return;
    }
    
    isRunning.current = true;
    
    const execute = async () => {
      try {
        await callback();
        hasRun.current = true;
      } catch (error) {
        console.error('useStrictModeOnce callback failed:', error);
        // Don't mark as run if it failed, allow retry
      } finally {
        isRunning.current = false;
      }
    };
    
    execute();
  }, deps);
  
  // Reset if dependencies change
  useEffect(() => {
    hasRun.current = false;
  }, deps);
};